import React from 'react';
import styled, { keyframes } from 'styled-components';

import useTranslation from 'hooks/use-translation';

import ROUTES from 'src/routes';
import { DutchieLogoColor } from 'assets/marketing/rebrand/dutchie-logo-color';
import { MenuHamburger } from 'assets/marketing/rebrand/menu-hamburger';
import { VisuallyHidden } from 'components/core/visually-hidden';
import { Clickable, DesktopOnly, MobileOnly } from 'shared/components';

import { AccountDropdown } from '../components/account-dropdown';
import { palette } from './palette';
import { GoldLink, PillLink } from './primitives';

type RedesignNavProps = {
  onToggleMobileMenu: () => void;
};

export function RedesignNav({ onToggleMobileMenu }: RedesignNavProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <NavWrap>
      <NavBar>
        <LogoLink href={ROUTES.ROOT} aria-label={t('common.dutchieHome', 'dutchie home')}>
          <DutchieLogoColor height='35px' flowerColor={palette.goldFirst} wordmarkColor={palette.cream} />
        </LogoLink>
        <DesktopCenter>
          <PillLink href={ROUTES.DISPENSARY_B2B}>{t('common.forBusiness', 'For business')}</PillLink>
          <Tagline>{t('home.redesign.navTagline', 'The #1 cannabis platform across North America.')}</Tagline>
          <GoldLink href={ROUTES.DISPENSARY_B2B}>{t('home.redesign.seeItInAction', 'See it in action')}</GoldLink>
        </DesktopCenter>
        <Right>
          <DesktopOnly display='block' mediaQuery='sm'>
            <AccountDropdown isOnLight={false} />
          </DesktopOnly>
          <MobileOnly display='block' mediaQuery='xs'>
            <HamburgerBox onClick={onToggleMobileMenu}>
              <MenuHamburger />
              <VisuallyHidden>{t('common.homepage.menu-icon-action', 'Open/Close/Toggle Menu')}</VisuallyHidden>
            </HamburgerBox>
          </MobileOnly>
        </Right>
      </NavBar>
      <DesktopOnly display='block' mediaQuery='sm'>
        <Stats>
          <StatsInner>
            <Stat>
              <div className='n'>6500+</div>
              <p>{t('home.redesign.statDispensaries', 'dispensaries trust dutchie')}</p>
            </Stat>
            <Stat>
              <div className='n'>$100B+</div>
              <p>{t('home.redesign.statSales', 'in all-time cumulative sales')}</p>
            </Stat>
            <Stat>
              <div className='n'>1M+</div>
              <p>{t('home.redesign.statTransactions', 'in daily transactions')}</p>
            </Stat>
          </StatsInner>
        </Stats>
      </DesktopOnly>
    </NavWrap>
  );
}

const shimmer = keyframes`
  0%   { background-position: -150% 0; }
  35%  { background-position: 250% 0; }
  100% { background-position: 250% 0; }
`;

const NavWrap = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  background: ${palette.plum};
`;

const NavBar = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 14px 32px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background: linear-gradient(
      75deg,
      transparent 0%,
      transparent 40%,
      rgba(255, 255, 255, 0.08) 50%,
      transparent 60%,
      transparent 100%
    );
    background-size: 200% 100%;
    background-repeat: no-repeat;
    background-position: -150% 0;
    opacity: 1;
    transition: opacity 0.25s ease;
  }

  ${NavWrap}:hover &::before,
  ${NavWrap}:focus-within &::before {
    opacity: 0;
  }

  @media (prefers-reduced-motion: no-preference) {
    &::before {
      animation: ${shimmer} 8s linear infinite;
    }
  }

  > * {
    position: relative;
    z-index: 1;
  }

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    justify-content: space-between;
    padding: 14px 20px;
  }
`;

const LogoLink = styled.a`
  display: inline-flex;
  align-items: center;
`;

const DesktopCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex: 1;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    display: none;
  }
`;

const Tagline = styled.p`
  margin: 0;
  font-size: 17px;
  font-weight: 500;
  color: ${palette.cream};

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    display: none;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

const HamburgerBox = styled(Clickable)`
  cursor: pointer;
  color: ${palette.cream};

  svg path {
    fill: ${palette.cream};
  }
`;

const Stats = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${palette.plum};
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-height 0.35s ease, opacity 0.25s ease, padding 0.35s ease;
  padding: 0 32px;

  ${NavWrap}:hover &,
  ${NavWrap}:focus-within & {
    max-height: 200px;
    opacity: 1;
    padding: 8px 32px 28px;
  }
`;

const StatsInner = styled.div`
  display: flex;
  justify-content: center;
  max-width: 960px;
  margin: 0 auto;
`;

const Stat = styled.div`
  text-align: center;
  padding: 0 40px;

  & + & {
    border-left: 1px solid rgba(255, 255, 255, 0.2);
  }

  .n {
    font-size: clamp(24px, 2.4vw, 32px);
    line-height: 1;
    color: ${palette.goldFirst};
    font-weight: 700;
  }

  p {
    margin: 8px 0 0;
    font-size: 14px;
    color: ${palette.cream};
    font-weight: 500;
  }
`;
